<template>
    <div class="contents">
        <div class="container-fluid">
            <div class="row">
                <div class="col-md-12">
                    <div class="breadcrumb-main user-member justify-content-sm-between">
                        <div class="d-flex align-items-center user-member__title">
                            <h4 class="text-capitalize fw-500 breadcrumb-title">
                                Monitor Resource Server
                            </h4>
                        </div>
                        <div class="action-btn d-flex align-items-center">
                            <label class="mr-3 mb-0">
                                <input type="checkbox" v-model="autoRefresh" class="mr-1">
                                Auto refresh (60s)
                            </label>
                            <button @click="load(true)" class="btn btn-secondary" :disabled="loading">
                                <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="error" class="alert alert-danger">{{ error }}</div>

            <div class="alert alert-info">
                Metrik read-only dari host <strong>163</strong> (API backend) dan <strong>103</strong> (frontend/DB/CDN).
                Data di-cache ~45 detik di Redis. Hanya admin.
            </div>

            <div class="row">
                <div v-for="srv in servers" :key="srv.id" class="col-lg-6 mb-30">
                    <div class="card global-shadow border-0 p-25 radius-xl h-100">
                        <div class="d-flex justify-content-between align-items-start mb-3">
                            <div>
                                <h5 class="mb-1">{{ srv.label }}</h5>
                                <small class="text-muted d-block">{{ srv.host }}</small>
                                <small class="text-muted">{{ srv.role }}</small>
                            </div>
                            <span class="badge" :class="srv.reachable ? 'badge-success' : 'badge-danger'">
                                {{ srv.reachable ? 'OK' : 'Error' }}
                            </span>
                        </div>

                        <p v-if="srv.error" class="text-danger small mb-2">{{ srv.error }}</p>

                        <template v-if="srv.reachable">
                            <div class="row mb-2">
                                <div class="col-6">
                                    <small class="text-muted d-block">Hostname</small>
                                    <strong>{{ srv.hostname || '—' }}</strong>
                                </div>
                                <div class="col-6">
                                    <small class="text-muted d-block">Uptime</small>
                                    <strong>{{ formatUptime(srv.uptime_sec) }}</strong>
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-4">
                                    <small class="text-muted d-block">Load (1/5/15)</small>
                                    <strong>{{ formatLoad(srv.load_avg) }}</strong>
                                </div>
                                <div class="col-4">
                                    <small class="text-muted d-block">CPU</small>
                                    <strong :class="cpuClass(srv.cpu_percent)">{{ formatPct(srv.cpu_percent) }}</strong>
                                </div>
                                <div class="col-4">
                                    <small class="text-muted d-block">RAM</small>
                                    <strong>{{ formatBytes(srv.mem_used_bytes) }} / {{ formatBytes(srv.mem_total_bytes) }}</strong>
                                    <div class="progress mt-1" style="height: 6px;">
                                        <div class="progress-bar" :class="memBarClass(srv)" :style="{ width: memPct(srv) + '%' }"></div>
                                    </div>
                                </div>
                            </div>

                            <div class="mb-2">
                                <small class="text-muted d-block mb-1">Disk</small>
                                <div v-for="d in srv.disk_mounts || []" :key="d.mount" class="mb-1">
                                    <div class="d-flex justify-content-between small">
                                        <span>{{ d.mount }}</span>
                                        <span>{{ formatBytes(d.used_bytes) }} / {{ formatBytes(d.total_bytes) }} ({{ diskPct(d) }}%)</span>
                                    </div>
                                    <div class="progress" style="height: 5px;">
                                        <div class="progress-bar bg-secondary" :style="{ width: diskPct(d) + '%' }"></div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="srv.services">
                                <small class="text-muted d-block">Services</small>
                                <span v-if="srv.services.nginx" class="badge mr-1" :class="serviceBadge(srv.services.nginx)">
                                    nginx: {{ srv.services.nginx }}
                                </span>
                                <span v-if="srv.services.pm2_processes !== undefined" class="badge badge-secondary mr-1">
                                    pm2 procs: {{ srv.services.pm2_processes }}
                                </span>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <p v-if="meta.cached_at" class="text-muted small text-right">
                {{ meta.from_cache ? 'Cache' : 'Live' }} · {{ formatTime(meta.cached_at) }}
                · TTL {{ meta.cache_ttl_sec }}s
            </p>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    data() {
        return {
            servers: [],
            loading: false,
            error: '',
            autoRefresh: true,
            timer: null,
            meta: {},
        };
    },
    mounted() {
        this.load(false);
        this.timer = setInterval(() => {
            if (this.autoRefresh) {
                this.load(false);
            }
        }, 60000);
    },
    beforeDestroy() {
        if (this.timer) {
            clearInterval(this.timer);
        }
    },
    methods: {
        async load(showToast) {
            this.loading = true;
            this.error = '';
            try {
                const res = await axios.get('admin/server-resources');
                if (res.data.success) {
                    this.servers = res.data.data.servers || [];
                    this.meta = {
                        cached_at: res.data.data.cached_at,
                        cache_ttl_sec: res.data.data.cache_ttl_sec,
                        from_cache: res.data.data.from_cache,
                    };
                    if (showToast) {
                        this.$toast.success('Metrik diperbarui');
                    }
                } else {
                    this.error = res.data.message || 'Gagal memuat data';
                }
            } catch (e) {
                this.error = e.response?.data?.message || e.message || 'Gagal memuat data';
            } finally {
                this.loading = false;
            }
        },
        formatUptime(sec) {
            if (!sec && sec !== 0) return '—';
            const d = Math.floor(sec / 86400);
            const h = Math.floor((sec % 86400) / 3600);
            const m = Math.floor((sec % 3600) / 60);
            if (d > 0) return `${d}d ${h}h`;
            if (h > 0) return `${h}h ${m}m`;
            return `${m}m`;
        },
        formatLoad(arr) {
            if (!arr || !arr.length) return '—';
            return arr.map(x => (x != null ? Number(x).toFixed(2) : '0')).join(' / ');
        },
        formatPct(v) {
            if (v == null || Number.isNaN(v)) return '—';
            return `${Math.round(v)}%`;
        },
        formatBytes(n) {
            if (n == null || n <= 0) return '—';
            const u = ['B', 'KB', 'MB', 'GB', 'TB'];
            let i = 0;
            let v = n;
            while (v >= 1024 && i < u.length - 1) {
                v /= 1024;
                i += 1;
            }
            return `${v.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
        },
        memPct(srv) {
            if (!srv.mem_total_bytes) return 0;
            return Math.min(100, Math.round((srv.mem_used_bytes / srv.mem_total_bytes) * 100));
        },
        memBarClass(srv) {
            const p = this.memPct(srv);
            if (p >= 90) return 'bg-danger';
            if (p >= 75) return 'bg-warning';
            return 'bg-success';
        },
        diskPct(d) {
            if (!d.total_bytes) return 0;
            return Math.min(100, Math.round((d.used_bytes / d.total_bytes) * 100));
        },
        cpuClass(p) {
            if (p >= 85) return 'text-danger';
            if (p >= 70) return 'text-warning';
            return 'text-success';
        },
        serviceBadge(state) {
            if (state === 'active') return 'badge-success';
            if (state === 'inactive' || state === 'failed') return 'badge-danger';
            return 'badge-secondary';
        },
        formatTime(iso) {
            if (!iso) return '';
            try {
                return new Date(iso).toLocaleString();
            } catch (_) {
                return iso;
            }
        },
    },
};
</script>
